import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { inviteService } from '../services/inviteService';
import { getInviteToken } from '../services/inviteAuth';
import { CandidateProfileCard } from '../components/CandidateProfileCard';
import { CandidateActionsList } from '../components/CandidateActionsList';
import { CandidateEventModal } from '../components/CandidateEventModal';
import type { InviteWorkflowEvent } from '../types';

export const CandidatePortalPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeEvent, setActiveEvent] = useState<InviteWorkflowEvent | null>(null);

  // Bounce to the expired screen when the candidate lands on this URL without
  // ever having clicked their magic link in this tab.
  useEffect(() => {
    if (!getInviteToken()) {
      navigate('/onboarding/invite/expired', { replace: true });
    }
  }, [navigate]);

  // Workflow state moves on every event — always-fresh read so the
  // candidate sees their next step appear immediately after submitting.
  const meQuery = useQuery({
    queryKey: ['invite-me'],
    queryFn: () => inviteService.getMe(),
    enabled: !!getInviteToken(),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const caseId = meQuery.data?.id;
  const workflowQuery = useQuery({
    queryKey: ['invite-workflow', caseId],
    queryFn: () => inviteService.getWorkflowState(caseId as number),
    enabled: typeof caseId === 'number',
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const handleEventSubmitted = async () => {
    setActiveEvent(null);
    // Small grace period so the backend write propagates before we read.
    await new Promise((resolve) => setTimeout(resolve, 300));
    await Promise.all([workflowQuery.refetch(), meQuery.refetch()]);
    queryClient.invalidateQueries({ queryKey: ['invite-event-meta'] });
  };

  if (meQuery.isLoading) {
    return (
      <div className="card">
        <div className="card-body text-center text-rgf-gray-500 py-12">
          Loading your application…
        </div>
      </div>
    );
  }

  if (meQuery.error) {
    return (
      <div className="card">
        <div className="card-body text-center py-12 space-y-3">
          <p className="text-sm text-rgf-gray-700">
            {(meQuery.error as Error)?.message || 'We could not load your application.'}
          </p>
          <Button onClick={() => meQuery.refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!meQuery.data) return null;

  return (
    <div className="space-y-4">
      <CandidateProfileCard
        candidate={meQuery.data}
        liveState={workflowQuery.data?.current_state}
      />
      <CandidateActionsList
        events={workflowQuery.data?.next_events ?? []}
        isLoading={workflowQuery.isLoading}
        isFetching={workflowQuery.isFetching}
        error={(workflowQuery.error as Error) || null}
        onSelect={setActiveEvent}
        onRefresh={() => workflowQuery.refetch()}
      />

      {activeEvent && typeof caseId === 'number' && (
        <CandidateEventModal
          caseId={caseId}
          event={activeEvent}
          isOpen={!!activeEvent}
          onClose={() => setActiveEvent(null)}
          onSuccess={handleEventSubmitted}
        />
      )}
    </div>
  );
};
